import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faBook, faEye, faFileContract, faGear, faQuestion, faSpinner } from '@fortawesome/pro-solid-svg-icons';

import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { MenuItem, MessageService, ToastMessageOptions } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { ButtonDirective } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { map } from 'rxjs';
import { AppConfig, LayoutService } from 'src/app/layout/service/app.layout.service';
import { Wiki } from 'src/app/rgs/components/admin/components/wiki/model/wiki';
import { WikiService } from 'src/app/rgs/components/admin/components/wiki/services/wiki.service';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { LicenseSwitchService } from 'src/app/shared/services/license-switch.service';
import { PermissionsService, haveUserReadOnlyRoleForMasterData } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { environment } from 'src/environments/environment';
import { AppBreadcrumbComponent } from '../breadcrumbs/app.breadcrumb.component';
import { AppSidebarComponent } from '../sidebar/app.sidebar.component';
import { NotificationsSidebarComponent } from './components/notifications-sidebar/notifications-sidebar.component';
import { NotesSidebarService } from './components/notifications-sidebar/services/notes-sidebar.service';
import { TaskStatus } from './components/tasks-sidebar/models/task.model';
import { TaskService } from './components/tasks-sidebar/services/task.service';
import { TasksSidebarComponent } from './components/tasks-sidebar/tasks-sidebar.component';

@Component({
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    AppBreadcrumbComponent,
    FaIconComponent,
    ButtonDirective,
    AppSidebarComponent,
    Avatar,
    Menu,
    NotificationsSidebarComponent,
    TasksSidebarComponent,
    AsyncPipe,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class AppTopbarComponent implements OnInit {
  /** Shared by the menu item's id, its expansion key and the collapse-on-close reset. */
  private static readonly LICENSE_KEY = 'topbar-licenses';

  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly permissionsService = inject(PermissionsService);
  public readonly layoutService = inject(LayoutService);
  public readonly el = inject(ElementRef);
  private readonly wikiService = inject(WikiService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly keycloak = inject(Keycloak);
  public readonly notesSidebarService = inject(NotesSidebarService);
  public readonly taskService = inject(TaskService);
  private readonly licenseSwitch = inject(LicenseSwitchService);

  public readonly faGear = faGear;
  public readonly faBook = faBook;
  public readonly faFileContract = faFileContract;
  public readonly faEye = faEye;
  public readonly faQuestion = faQuestion;
  public readonly faSpinner = faSpinner;

  public loadingWiki = this.wikiService.loadingWiki;

  public config$ = this.layoutService.config$;

  public readonly menuButton = viewChild<ElementRef>('menubutton');
  public readonly appSidebar = viewChild.required(AppSidebarComponent);

  private readonly notificationsSidebar = viewChild.required(NotificationsSidebarComponent);

  public readonly username = signal('');

  public notesLength$ = this.notesSidebarService.items$.pipe(
    map(
      (items) =>
        items.filter(
          (note) =>
            !ArrayUtils.ensureArray(note?.gelezen)
              .map((read) => read.gebruiker)
              .includes(this.userName),
        ).length,
    ),
  );
  public tasksLength$ = this.taskService.items$.pipe(
    map(
      (tasks) =>
        tasks.filter((task) => task.reportStatus === TaskStatus.RUNNING || task.reportStatus === TaskStatus.QUEUED)
          .length,
    ),
  );
  public taskStatus$ = this.taskService.taskStatus$;
  public runningTaskId = this.taskService.runningTaskId;
  public user$ = this.permissionsService.user$;

  private readonly notesCount = toSignal(this.notesLength$, { initialValue: 0 });
  private readonly tasksCount = toSignal(this.tasksLength$, { initialValue: 0 });
  private readonly activeLicense = this.permissionsService.activeLicense;

  private readonly labels = this.translateService.translate([
    'topbar.profile',
    'topbar.notifications',
    'topbar.reports',
    'topbar.licentie',
    'topbar.logout',
  ]) as Signal<Record<string, string>>;

  public readonly activeLicenseLabel = computed(() => this.toTitleCase(this.activeLicense() ?? ''));

  public readonly menuOpen = signal(false);

  public readonly licenseExpandedKeys = signal<Record<string, boolean>>({ [AppTopbarComponent.LICENSE_KEY]: false });

  public readonly profileMenuItems = computed<MenuItem[]>(() => {
    const labels = this.labels();
    const active = this.activeLicense();
    const licenses = this.licenses;
    const kibUser = this.isKibUser;
    const notes = this.notesCount();
    const tasks = this.tasksCount();

    return [
      {
        id: 'topbar-profile',
        label: labels['topbar.profile'],
        icon: 'fa fa-user',
        command: () => this.onProfileButtonClick(),
      },
      {
        id: 'topbar-notifications',
        label: labels['topbar.notifications'],
        icon: 'fa fa-inbox',
        visible: !kibUser,
        badge: notes > 0 ? String(notes) : undefined,
        command: () => this.notificationsSidebar().notificationsSidebarVisible.set(true),
      },
      {
        id: 'topbar-reports',
        label: labels['topbar.reports'],
        icon: 'fa fa-book',
        visible: !kibUser,
        badge: tasks > 0 ? String(tasks) : undefined,
        command: () => this.showTasksSidebar(),
      },
      {
        id: AppTopbarComponent.LICENSE_KEY,
        key: AppTopbarComponent.LICENSE_KEY,
        label: labels['topbar.licentie'],
        icon: 'fa fa-id-card',
        toggleable: true,
        visible: !kibUser && licenses.length > 1,
        items: licenses.map((license) => ({
          id: `topbar-license-${license}`,
          label: this.toTitleCase(license),
          icon: license === active ? 'fa fa-circle-check' : 'fa fa-building',
          styleClass: license === active ? 'topbar-license-active' : undefined,
          disabled: license === active,
          command: () => this.changeLicense(license),
        })),
      },
      { separator: true },
      {
        id: 'topbar-logout',
        label: labels['topbar.logout'],
        icon: 'fa fa-power-off',
        command: () => this.logout(),
      },
    ];
  });

  public collapseLicenses(): void {
    this.licenseExpandedKeys.set({ [AppTopbarComponent.LICENSE_KEY]: false });
  }

  private toTitleCase(value: string): string {
    return value.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  public ngOnInit(): void {
    this.taskStatus$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((task) => {
      let message: ToastMessageOptions | undefined;
      switch (task?.reportStatus) {
        case TaskStatus.COMPLETE:
          this.messageService.clear('task-running');
          message = {
            key: 'task-complete',
            data: task,
            severity: 'success',
            summary: this.translateService.instant('reportTasks.messages.complete.summary'),
            detail: this.translateService.instant('reportTasks.messages.complete.detail', {
              name: task?.reportName ? task?.reportName : '',
            }),
            life: 5000,
          };
          break;
        case TaskStatus.RUNNING:
          if (this.taskService.runningTaskId !== task?.uuid) {
            message = {
              closable: false,
              key: 'task-running',
              data: task,
              severity: 'info',
              summary: this.translateService.instant('reportTasks.messages.running.summary'),
              detail: this.translateService.instant('reportTasks.messages.running.detail', {
                name: task?.reportName ? task?.reportName : '',
              }),
              icon: 'fa-solid fa-circle-notch fa-spin',
              sticky: false,
            };
          }
          break;
        case TaskStatus.FAIL:
          this.messageService.clear('task-running');
          message = {
            data: task,
            severity: 'error',
            summary: this.translateService.instant('reportTasks.messages.fail.summary'),
            detail: this.translateService.instant('reportTasks.messages.fail.detail', {
              name: task?.reportName ? task?.reportName : '',
            }),
            life: 5000,
          };
          break;
      }
      if (message) {
        this.messageService.add(message);
      }
    });
    this.keycloak.loadUserProfile().then((value) => {
      let keycloakLocale = 'en';

      const locale = value?.attributes?.['locale'] as string[] | undefined;
      if (locale && locale.length > 0) {
        keycloakLocale = locale[0];
      }

      localStorage.setItem('locale', keycloakLocale);
      this.translateService.use(keycloakLocale);

      const name = [value.firstName, value.lastName].filter(Boolean).join(' ');
      this.username.set(this.getInitials(name, 2));
    });

    this.wikiService.currentWiki$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selected: Wiki | undefined) => {
      if (selected?.active) {
        this.loadedWikiPage = selected;
      } else {
        this.loadedWikiPage = undefined;
      }
    });
  }
  public loadedWikiPage?: Wiki;

  public onProfileButtonClick() {
    this.router.navigateByUrl('/user-profile');
  }

  public logout() {
    window.sessionStorage.removeItem('environment');
    window.sessionStorage.removeItem('tenant');
    this.keycloak.logout();
  }

  public showTasksSidebar() {
    this.taskService.taskSidebarVisible$.next(true);
  }

  private getInitials(name: string, size: number): string {
    name = name.trim();

    if (!name) {
      return '';
    }

    const initials = name.split(' ');

    if (size && size < initials.length) {
      return this.constructInitials(initials.slice(0, size));
    } else {
      return this.constructInitials(initials);
    }
  }

  /**
   * Iterates a person's name string to get the initials of each word in uppercase.
   */
  private constructInitials(elements: string[]): string {
    if (!elements?.length) {
      return '';
    }

    return elements
      .filter((element) => element && element.length > 0)
      .map((element) => element[0].toUpperCase())
      .join('');
  }

  public changeLicense(license: string) {
    this.licenseSwitch
      .switchTo(license)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.router.navigate(['/']),
        error: (err: RgsHttpErrorResponse) => ToastUtils.showError(this.messageService, null, err.error),
      });
  }

  public isCompact(config: AppConfig) {
    return config.menuMode === 'compact';
  }

  public toggleSidebarMode() {
    this.layoutService.config$.next({
      ...this.layoutService.config$.getValue(),
      menuMode: this.isCompact(this.layoutService.config$.getValue()) ? 'static' : 'compact',
    });
  }

  public get userRoles() {
    const tokenParsed = this.keycloak.tokenParsed;
    return ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
  }

  public get licenses(): string[] {
    return ArrayUtils.ensureArray(this.permissionsService.userProfile()?.attributes?.licentie).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  public get license() {
    return this.permissionsService.license;
  }

  public get environment() {
    switch (environment.springAppUrl) {
      case 'http://localhost:8080':
        return this.translateService.instant('environments.local');
      case 'https://beta.service.rgsplus.nl':
        return this.translateService.instant('environments.beta');
      case 'https://v3.service.rgsplus.nl':
        return this.translateService.instant('environments.production');
      default:
        return this.translateService.instant('environments.notKnow');
    }
  }

  public get class() {
    switch (environment.springAppUrl) {
      case 'http://localhost:8080':
        return 'local';
      case 'https://beta.service.rgsplus.nl':
        return 'beta';
      case 'https://v3.service.rgsplus.nl':
        return 'production';
      default:
        return 'notKnow';
    }
  }

  public get showEnvironmentTile(): boolean {
    return this.class !== 'production';
  }

  public get environmentColorClasses(): string {
    switch (this.class) {
      case 'local':
        return 'bg-amber-400 text-amber-950';
      case 'beta':
        return 'bg-red-500 text-white';
      case 'production':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  }

  public get userName(): string {
    return this.keycloak.idTokenParsed?.['name'];
  }

  public get isUserInMaster() {
    return this.router.url.includes('/master');
  }

  public get haveUserReadOnlyRoleForMasterData() {
    return haveUserReadOnlyRoleForMasterData;
  }

  public get isKibUser() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    const kibRoles = new Set(['kib_admin', 'kib_user']);

    const licenses = ArrayUtils.ensureArray(this.permissionsService.userProfile()?.attributes?.licentie);

    return licenses.length === 0 && roles.some((role) => kibRoles.has(role));
  }

  public get logoSrc(): string {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    const isKibCore = roles.includes('kib_core');

    return isKibCore ? 'assets/layout/images/logo_rgsnl.svg' : 'assets/layout/images/logo.svg';
  }

  public openWiki() {
    if (!this.loadedWikiPage) {
      return;
    }
    this.wikiService.getWikiPageAndOpenDialog(this.loadedWikiPage.pageId);
  }

  public hasRgsAdminRole() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    return roles.includes('rgs_admin');
  }
}
